import { ChangeEvent, JSX, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useScrollTrigger from "@mui/material/useScrollTrigger";

import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

import { stringToDate } from "utils/DateUtils";
import FeedbackIconButton from "utils/FeedbackIconButton";
import Filter from "utils/Filter";
import { asTitle } from "utils/Utils";
import DatePicker from "utils/elements/DatePicker";

import { getLoggedInUser } from "authentication/AuthenticationService";
import {
	EvaluationRight,
	InformationRight,
	ReportRight,
	Role
} from "core/blueprint/trajectory/TrajectoryBlueprintService";

import {
	TrajectoryAccess,
	TrajectoryInfo,
	TrajectoryRight
} from "./TrajectoryService";

// While tab content that owns its own edit state (the evaluation) is being
// edited, the pinned row belongs to that edit: the trajectory's edit and cancel
// buttons and the as-of picker step out, and the content portals its own
// controls into these two slots, which take their places. The actions slot
// sits where the trajectory's buttons were, the fields slot where the as-of
// picker was. Both are always in the DOM, so the portal can find them before
// the parent has re-rendered, and hidden while nothing is being edited.
export const ACTIONBAR_ACTIONS_SLOT_ID = "trajectory-actionbar-actions-slot";
export const ACTIONBAR_FIELDS_SLOT_ID = "trajectory-actionbar-fields-slot";

export interface TrajectoryActionbarProps {
	edit: boolean;
	dirty: boolean;
	info: TrajectoryInfo;
	access: TrajectoryAccess;

	onChangeFilter: (filter: string) => void;
	onChangeTitle: (title: string) => void;

	onEdit: () => void;
	onSave: () => Promise<void>;
	onExit: () => void;

	// The day whose state the dashboard and the overview show; null means
	// the current state. asOfDates are the days that carry an evaluation,
	// marked in the picker.
	asOf: Date | null;
	asOfDates: Date[];
	onChangeAsOf: (date: Date | null) => void;

	// True while the tab content (an evaluation) is being edited; see the
	// slot ids above for what the row does then.
	contentEditing: boolean;
}

function TrajectoryViewActionbar(props: TrajectoryActionbarProps): JSX.Element {
	const { t } = useTranslation();

	// 24 is the top padding of the Container in Page.tsx, so the trigger flips
	// exactly when the row reaches the AppBar.
	const stuck = useScrollTrigger({ disableHysteresis: true, threshold: 24 });

	function isAllowedToEditMetadata(): boolean {
		return props.info.rights.includes(TrajectoryRight.METADATA);
	}

	function isAllowedToEditAccess(): boolean {
		return props.info.rights.includes(TrajectoryRight.RIGHTS);
	}

	function getUserRoles(): Role[] {
		const user = getLoggedInUser();
		const userRoles =
			props.access.users.find(u => u.user === user)?.roles ?? [];

		return props.access.roles.filter(role => userRoles.includes(role.name));
	}

	function isAllowedToEvaluate(roles: Role[]): boolean {
		return roles.some(
			role => role.evaluationRights[EvaluationRight.WRITE] === true
		);
	}

	function isAllowedToEditFields(roles: Role[]): boolean {
		return roles.some(role =>
			Object.values(role.informationRights).some(
				rights => rights[InformationRight.WRITE] === true
			)
		);
	}

	function isAllowedToEditReports(roles: Role[]): boolean {
		return roles.some(
			role =>
				Object.values(role.reportRights).some(
					rights => rights[""]?.[ReportRight.WRITE] === true
				) ||
				Object.values(role.reportRights)
					.flatMap(entry => Object.entries(entry))
					.filter(entry => entry[0] !== "")
					.map(entry => entry[1])
					.some(rights => rights[ReportRight.READ] === true)
		);
	}

	function isAllowedToEdit(): boolean {
		if (isAllowedToEditMetadata() || isAllowedToEditAccess()) {
			return true;
		}

		const userRoles = getUserRoles();
		if (userRoles.length === 0) {
			return false;
		}

		return (
			isAllowedToEditFields(userRoles) ||
			isAllowedToEvaluate(userRoles) ||
			isAllowedToEditReports(userRoles)
		);
	}

	function isActive(): boolean {
		if (props.info.activeUntil === null) {
			return false;
		}

		const expiryDate = stringToDate(props.info.activeUntil);
		if (expiryDate === null) {
			return false;
		}

		const now = new Date();
		return expiryDate > now;
	}

	const changeTitle = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement>): void => {
			props.onChangeTitle(event.target.value);
		},
		[props.onChangeTitle]
	);

	return (
		<>
			<Grid
				sx={theme => ({
					displayPrint: "none",
					position: "sticky",
					// The AppBar is fixed, so this row sits one AppBar below
					// the viewport top. Not theme.mixins.toolbar: that claims
					// 48px in landscape, but MenuBar's outlined Select forces
					// the bar to 56px there, which would overlap by 8px.
					top: { xs: 56, sm: 64 },
					zIndex: theme.zIndex.appBar,
					backgroundColor: "background.default",
					borderBottom: 1,
					borderColor: stuck ? "divider" : "transparent",
					// A CSS gap, not Grid spacing: spacing's negative margins
					// would push the pinned background past the Container.
					columnGap: 2
				})}
				container
				alignItems="center"
				justifyContent="space-between">
				{!props.contentEditing && (
					<Grid item>
						<Grid container wrap="nowrap">
							<Grid item>
								{isAllowedToEdit() && isActive() && (
									<>
										{!props.edit ? (
											<Tooltip title={t("edit")}>
												<IconButton
													id="edit-button"
													aria-label="edit"
													onClick={props.onEdit}
													size="large">
													<EditIcon />
												</IconButton>
											</Tooltip>
										) : (
											<Tooltip title={t("save")}>
												<span>
													<FeedbackIconButton
														disabled={!props.dirty}
														onClick={props.onSave}
														size="large">
														<SaveIcon />
													</FeedbackIconButton>
												</span>
											</Tooltip>
										)}
									</>
								)}
							</Grid>
							<Grid item>
								<Tooltip title={t("cancel") ?? ""}>
									<IconButton
										id="cancel-button"
										aria-label="cancel"
										onClick={props.onExit}
										size="large">
										<CloseIcon />
									</IconButton>
								</Tooltip>
							</Grid>
						</Grid>
					</Grid>
				)}
				<Grid
					item
					id={ACTIONBAR_ACTIONS_SLOT_ID}
					sx={{
						display: props.contentEditing ? "flex" : "none",
						alignItems: "center"
					}}
				/>
				<Grid
					item
					xs={12}
					sm
					sx={{ order: { xs: -1, sm: 0 }, minWidth: 0 }}>
					{props.edit && isAllowedToEditMetadata() ? (
						<TextField
							id="title"
							type="text"
							label={t("title")}
							required
							fullWidth
							value={props.info.name}
							onChange={changeTitle}
							variant="standard"
						/>
					) : (
						<Tooltip title={props.info?.name ?? ""}>
							<Typography
								id="title"
								variant="h5"
								align="center"
								sx={{
									display: "-webkit-box",
									WebkitBoxOrient: "vertical",
									WebkitLineClamp: { xs: 2, sm: 1 },
									overflow: "hidden",
									fontSize: { xs: "1rem", sm: "1.5rem" },
									lineHeight: 1.3
								}}>
								{props.info?.name ?? ""}
							</Typography>
						</Tooltip>
					)}
				</Grid>
				<Grid
					item
					id={ACTIONBAR_FIELDS_SLOT_ID}
					xs="auto"
					sx={{
						display: props.contentEditing ? "flex" : "none",
						alignSelf: "flex-end"
					}}
				/>
				{!props.contentEditing && (
					<Grid item xs="auto" sx={{ alignSelf: "flex-end" }}>
						<DatePicker
							label={asTitle(t("asOf"))}
							value={props.asOf}
							disableFuture
							highlightedDates={props.asOfDates}
							inputProps={{
								variant: "standard",
								sx: { width: { xs: 130, sm: 150 } }
							}}
							onChange={props.onChangeAsOf}
						/>
					</Grid>
				)}
				<Grid item xs="auto" sx={{ alignSelf: "flex-end" }}>
					<Filter onChange={props.onChangeFilter} />
				</Grid>
			</Grid>

			<Grid sx={{ display: "none", displayPrint: "block" }} container>
				<Grid item>
					<Typography id="title" variant="h5" align="center">
						{props.info?.name ?? ""}
					</Typography>
				</Grid>
			</Grid>
		</>
	);
}

export default TrajectoryViewActionbar;
